import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CURRENCIES, formatMoney } from "@/lib/helpers";
import {
  Loader2,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Wallet,
  CalendarDays,
  FileText,
  Upload,
  X,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";

const emptyDay = () => ({ title: "", date: "", destination: "", activities: [] });
const emptyActivity = () => ({ time: "", title: "", notes: "" });
const emptyPayment = () => ({ date: new Date().toISOString().slice(0, 10), amount: "", note: "", type: "payment" });

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fieldCls = "bg-input border-border h-11 focus-visible:ring-primary";

export default function ItineraryEditor({ open, onOpenChange, itinerary, clients, defaultClientId, onSaved }) {
  const editing = !!itinerary;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDay, setOpenDay] = useState(0);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docCategory, setDocCategory] = useState("Voucher");
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverVer, setCoverVer] = useState(0);

  useEffect(() => {
    if (!open) return;
    setForm({
      client_id: itinerary?.client_id || defaultClientId || "",
      title: itinerary?.title || "",
      destination: itinerary?.destination || "",
      start_date: itinerary?.start_date || "",
      end_date: itinerary?.end_date || "",
      cover_image: itinerary?.cover_image || "",
      cover_storage_path: itinerary?.cover_storage_path || "",
      currency: itinerary?.currency || "USD",
      total_amount: itinerary?.total_amount ?? "",
      remaining_balance: itinerary?.remaining_balance ?? "",
      notes: itinerary?.notes || "",
      days: itinerary?.days ? JSON.parse(JSON.stringify(itinerary.days)) : [],
      payments: itinerary?.payments ? JSON.parse(JSON.stringify(itinerary.payments)) : [],
    });
    setOpenDay(0);
    setDocs(itinerary?.documents || []);
  }, [open, itinerary, defaultClientId]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const paidSum = form.payments.filter((p) => p.type === "payment").reduce((s, p) => s + Number(p.amount || 0), 0);
  const chargeSum = form.payments.filter((p) => p.type === "charge").reduce((s, p) => s + Number(p.amount || 0), 0);
  const computedRemaining = Number(form.total_amount || 0) + chargeSum - paidSum;

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !itinerary) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", docCategory);
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/itineraries/${itinerary.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [...d, data]);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteDoc = async (id) => {
    try {
      await api.delete(`/admin/itineraries/${itinerary.id}/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Document removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openDoc = async (id) => {
    try {
      const { data } = await api.get(`/itineraries/${itinerary.id}/documents/${id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) {
      toast.error("Could not open document");
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !itinerary) return;
    const fd = new FormData();
    fd.append("file", file);
    setCoverUploading(true);
    try {
      const { data } = await api.post(`/admin/itineraries/${itinerary.id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_storage_path: data.cover_storage_path }));
      setCoverVer(Date.now());
      toast.success("Cover photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const coverSrc = form.cover_storage_path
    ? `${BACKEND_URL}/api/itineraries/${itinerary?.id}/cover?v=${coverVer}`
    : form.cover_image || "";

  // days
  const addDay = () => { setForm((f) => ({ ...f, days: [...f.days, emptyDay()] })); setOpenDay(form.days.length); };
  const removeDay = (i) => setForm((f) => ({ ...f, days: f.days.filter((_, idx) => idx !== i) }));
  const setDay = (i, k, v) => setForm((f) => ({ ...f, days: f.days.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) }));
  const addActivity = (di) => setForm((f) => ({ ...f, days: f.days.map((d, idx) => (idx === di ? { ...d, activities: [...d.activities, emptyActivity()] } : d)) }));
  const removeActivity = (di, ai) => setForm((f) => ({ ...f, days: f.days.map((d, idx) => (idx === di ? { ...d, activities: d.activities.filter((_, x) => x !== ai) } : d)) }));
  const setActivity = (di, ai, k, v) => setForm((f) => ({ ...f, days: f.days.map((d, idx) => (idx === di ? { ...d, activities: d.activities.map((a, x) => (x === ai ? { ...a, [k]: v } : a)) } : d)) }));

  // payments
  const addPayment = () => setForm((f) => ({ ...f, payments: [...f.payments, emptyPayment()] }));
  const removePayment = (i) => setForm((f) => ({ ...f, payments: f.payments.filter((_, idx) => idx !== i) }));
  const setPayment = (i, k, v) => setForm((f) => ({ ...f, payments: f.payments.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));

  const submit = async () => {
    if (!form.client_id) return toast.error("Select a client");
    if (!form.title) return toast.error("Enter a trip title");
    setLoading(true);
    const payload = {
      ...form,
      total_amount: Number(form.total_amount || 0),
      remaining_balance: Number(form.remaining_balance || 0),
      payments: form.payments.map((p) => ({ ...p, amount: Number(p.amount || 0) })),
    };
    try {
      if (editing) {
        await api.put(`/admin/itineraries/${itinerary.id}`, payload);
        toast.success("Itinerary updated");
      } else {
        await api.post("/admin/itineraries", payload);
        toast.success("Itinerary created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="itinerary-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light tracking-tighter">
            {editing ? "Edit itinerary" : "New itinerary"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-8 mt-2">
          {/* Basics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-1">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)} disabled={editing}>
                <SelectTrigger data-testid="itinerary-client-select" className={fieldCls}>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Trip title</Label>
              <Input data-testid="itinerary-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="A Week in Marrakech" className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Destination</Label>
              <Input data-testid="itinerary-destination-input" value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Morocco" className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Cover photo</Label>
              {editing ? (
                <div className="flex items-center gap-3">
                  <div className="w-24 h-16 rounded-md overflow-hidden bg-secondary/40 border border-border shrink-0 flex items-center justify-center">
                    {coverSrc ? (
                      <img src={coverSrc} alt="cover" className="w-full h-full object-cover" data-testid="cover-preview" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-full border border-primary/40 text-primary hover:bg-primary/10 px-4 py-2 text-sm cursor-pointer transition-colors" data-testid="upload-cover-label">
                    {coverUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {coverUploading ? "Uploading…" : "Upload cover"}
                    <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.heic,.gif" onChange={uploadCover} data-testid="cover-file-input" disabled={coverUploading} />
                  </label>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Save the itinerary first to upload a cover photo.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">Start date</Label>
              <Input data-testid="itinerary-start-input" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={fieldCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase text-muted-foreground">End date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={fieldCls} />
            </div>
          </div>

          {/* Balance */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-primary" />
              <h3 className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Balance</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {Object.keys(CURRENCIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Total amount</Label>
                <Input type="number" step="0.01" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} className={fieldCls} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Remaining (auto)</Label>
                <div
                  data-testid="itinerary-balance-display"
                  className="h-11 flex items-center px-3 rounded-md bg-secondary/40 border border-border font-serif text-xl tracking-tighter gold-text"
                >
                  {formatMoney(computedRemaining, form.currency)}
                </div>
              </div>
            </div>

            {/* Payment log */}
            <div className="mt-5 space-y-3">
              {form.payments.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Select value={p.type} onValueChange={(v) => setPayment(i, "type", v)}>
                    <SelectTrigger className={`${fieldCls} col-span-3 h-10`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="payment">Payment</SelectItem>
                      <SelectItem value="charge">Charge</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={p.date} onChange={(e) => setPayment(i, "date", e.target.value)} className={`${fieldCls} col-span-3 h-10`} />
                  <Input type="number" step="0.01" placeholder="Amount" value={p.amount} onChange={(e) => setPayment(i, "amount", e.target.value)} className={`${fieldCls} col-span-2 h-10`} />
                  <Input placeholder="Note" value={p.note} onChange={(e) => setPayment(i, "note", e.target.value)} className={`${fieldCls} col-span-3 h-10`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removePayment(i)} className="col-span-1 text-muted-foreground hover:text-destructive-foreground">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addPayment} data-testid="add-payment-btn" className="rounded-full border-primary/40 text-primary hover:bg-primary/10">
                <Plus className="w-4 h-4 mr-1" /> Add payment entry
              </Button>
            </div>
          </div>

          {/* Documents */}
          {editing ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-primary" />
                <h3 className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Trip Documents</h3>
              </div>
              <div className="space-y-2 mb-4">
                {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents yet.</p>}
                {docs.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 bg-secondary/40 border border-border rounded-lg px-4 py-3" data-testid={`doc-row-${doc.id}`}>
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <button type="button" onClick={() => openDoc(doc.id)} className="text-sm text-foreground truncate hover:text-primary text-left block w-full">
                        {doc.original_filename}
                      </button>
                      <p className="text-xs text-muted-foreground">{doc.category}</p>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => openDoc(doc.id)} className="text-muted-foreground hover:text-primary h-8 w-8">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => deleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`} className="text-muted-foreground hover:text-destructive-foreground h-8 w-8">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Select value={docCategory} onValueChange={setDocCategory}>
                  <SelectTrigger className={`${fieldCls} w-40`} data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="Voucher">Voucher</SelectItem>
                    <SelectItem value="Ticket">Ticket</SelectItem>
                    <SelectItem value="Passport">Passport</SelectItem>
                    <SelectItem value="Hotel">Hotel</SelectItem>
                    <SelectItem value="Insurance">Insurance</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <label className="inline-flex items-center gap-2 rounded-full border border-primary/40 text-primary hover:bg-primary/10 px-4 py-2 text-sm cursor-pointer transition-colors" data-testid="upload-document-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading…" : "Upload document"}
                  <input type="file" className="hidden" onChange={uploadDoc} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" data-testid="document-file-input" disabled={uploading} />
                </label>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Save the itinerary first to attach trip documents.</p>
          )}

          {/* Days */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="w-4 h-4 text-primary" />
              <h3 className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Trip Days</h3>
            </div>
            <div className="space-y-3">
              {form.days.map((day, di) => (
                <div key={di} className="border border-border rounded-lg overflow-hidden" data-testid={`day-block-${di}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-secondary/40 cursor-pointer" onClick={() => setOpenDay(openDay === di ? -1 : di)}>
                    <span className="text-sm">
                      <span className="text-primary tracking-widest uppercase text-xs mr-2">Day {di + 1}</span>
                      {day.title || "Untitled"}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); removeDay(di); }} className="text-muted-foreground hover:text-destructive-foreground h-8 w-8">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {openDay === di ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>
                  {openDay === di && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Input placeholder="Day title" value={day.title} onChange={(e) => setDay(di, "title", e.target.value)} className={fieldCls} data-testid={`day-title-${di}`} />
                        <Input type="date" value={day.date} onChange={(e) => setDay(di, "date", e.target.value)} className={fieldCls} />
                        <Input placeholder="Destination" value={day.destination} onChange={(e) => setDay(di, "destination", e.target.value)} className={fieldCls} />
                      </div>
                      <div className="space-y-2 pl-1">
                        {day.activities.map((a, ai) => (
                          <div key={ai} className="grid grid-cols-12 gap-2 items-center">
                            <Input placeholder="Time" value={a.time} onChange={(e) => setActivity(di, ai, "time", e.target.value)} className={`${fieldCls} col-span-2 h-10`} />
                            <Input placeholder="Activity" value={a.title} onChange={(e) => setActivity(di, ai, "title", e.target.value)} className={`${fieldCls} col-span-4 h-10`} />
                            <Input placeholder="Notes" value={a.notes} onChange={(e) => setActivity(di, ai, "notes", e.target.value)} className={`${fieldCls} col-span-5 h-10`} />
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeActivity(di, ai)} className="col-span-1 text-muted-foreground hover:text-destructive-foreground h-10 w-10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" onClick={() => addActivity(di)} className="text-primary hover:bg-primary/10 h-9">
                          <Plus className="w-4 h-4 mr-1" /> Add activity
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addDay} data-testid="add-day-btn" className="rounded-full border-primary/40 text-primary hover:bg-primary/10">
                <Plus className="w-4 h-4 mr-1" /> Add day
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Personal note (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="bg-input border-border focus-visible:ring-primary" placeholder="A warm welcome message for your client…" />
          </div>

          <div className="flex justify-end gap-3 pb-2 sticky bottom-0 bg-card pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
            <Button
              onClick={submit}
              data-testid="itinerary-save-btn"
              disabled={loading}
              className="rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform gold-glow px-8"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save itinerary" : "Create itinerary"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
